import { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import * as LocalAuthentication from 'expo-local-authentication';
import { ERRORS } from '../constants/errors';
import {
  SecureStorageService,
  TSecureStorageOption,
} from '../integrations/secure-storage/secure-storage.service';

export enum BiometricsType {
  FACE_ID = 'face_id',
  TOUCH_ID = 'touch_id',
}

export const useBiometrics = () => {
  const [facialRecognitionAvailable, setFacialRecognitionAvailable] =
    useState(false);
  const [fingerprintAvailable, setFingerprintAvailable] = useState(false);
  const [irisAvailable, setIrisAvailable] = useState(false);
  const [isBiometricsActive, setIsBiometricsActive] = useState(false); // When we have biometrics available on the device
  const [biometricsEnabled, setBiometricsEnabled] = useState(false); // When we enable it for our app

  const biometricsType: BiometricsType | null = useMemo(() => {
    if (Platform.OS === 'ios') {
      return facialRecognitionAvailable
        ? BiometricsType.FACE_ID
        : fingerprintAvailable
          ? BiometricsType.TOUCH_ID
          : null;
    }
    return fingerprintAvailable
      ? BiometricsType.TOUCH_ID
      : facialRecognitionAvailable || irisAvailable
        ? BiometricsType.FACE_ID
        : null;
  }, [facialRecognitionAvailable, fingerprintAvailable, irisAvailable]);

  const getAuthDataFromSecureStorage = useCallback(
    async (key: TSecureStorageOption) => {
      return await SecureStorageService.getDataFromSecureStorage(key);
    },
    []
  );

  useEffect(() => {
    const checkBiometricsEnabled = async () => {
      const biometricsEnabled =
        await SecureStorageService.getDataFromSecureStorage(
          'biometricsEnabled'
        );
      if (biometricsEnabled) {
        setBiometricsEnabled(true);
      }
    };
    checkBiometricsEnabled();
  }, []);

  useEffect(() => {
    const checkDeviceForBiometricsAvailability = async () => {
      const deviceIsBiometricsCapable =
        await LocalAuthentication.hasHardwareAsync();
      const deviceHasBiometricsEnrolled =
        await LocalAuthentication.isEnrolledAsync();
      setIsBiometricsActive(
        deviceIsBiometricsCapable && deviceHasBiometricsEnrolled
      );

      const supportedAuthTypes =
        await LocalAuthentication.supportedAuthenticationTypesAsync();

      if (supportedAuthTypes && supportedAuthTypes.length) {
        setFacialRecognitionAvailable(
          supportedAuthTypes.includes(
            LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION
          )
        );
        setFingerprintAvailable(
          supportedAuthTypes.includes(
            LocalAuthentication.AuthenticationType.FINGERPRINT
          )
        );
        setIrisAvailable(
          supportedAuthTypes.includes(
            LocalAuthentication.AuthenticationType.IRIS
          )
        );
      }
    };
    checkDeviceForBiometricsAvailability();
  }, []);

  const authenticateWithBiometrics = useCallback(async () => {
    try {
      const result = await LocalAuthentication.authenticateAsync();

      if (result.success) {
        const email = await getAuthDataFromSecureStorage('email');
        const password = await getAuthDataFromSecureStorage('password');
        return { email, password };
      }
      return null;
    } catch (error) {
      return null;
    }
  }, [getAuthDataFromSecureStorage]);

  const enableBiometrics = async (): Promise<boolean> => {
    try {
      const result = await LocalAuthentication.authenticateAsync();

      if (result.success) {
        await SecureStorageService.saveDataToSecureStorage({
          biometricsEnabled: 'true',
        });
        setBiometricsEnabled(true);
        return true;
      }
      return false;
    } catch (error) {
      throw new Error(ERRORS.BIOMETRICS_ACTIVATION_FAILED);
    }
  };

  const disableBiometrics = async () => {
    await SecureStorageService.deleteDataFromSecureStorage('biometricsEnabled');
    setBiometricsEnabled(false);
  };

  return {
    biometricsAvailable:
      isBiometricsActive &&
      (facialRecognitionAvailable || fingerprintAvailable || irisAvailable),
    facialRecognitionAvailable,
    fingerprintAvailable,
    irisAvailable,
    biometricsType,
    authenticateWithBiometrics,
    enableBiometrics,
    disableBiometrics,
    biometricsEnabled,
  };
};
